import { motion, useInView, useReducedMotion } from "motion/react";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  CalendarDays,
  Camera,
  Check,
  ChevronLeft,
  ChevronRight,
  Sparkles,
} from "@/components/icons";

type CalendarEvent = {
  day: number;
  time: string;
  title: string;
  detail?: string;
  kind: "product" | "client" | "launch";
  moved?: boolean;
};

type StoryState = {
  entered: boolean;
  reduceMotion: boolean;
};

const EVENTS: CalendarEvent[] = [
  { day: 1, time: "10:00", title: "Kickoff", detail: "Meet · 45 min", kind: "client" },
  { day: 2, time: "All day", title: "Product direction", kind: "product" },
  { day: 4, time: "15:30", title: "First screens", detail: "Review · 30 min", kind: "client" },
  { day: 5, time: "11:00", title: "Flow review", kind: "product", moved: true },
  { day: 8, time: "16:00", title: "Working build", detail: "Meet · 30 min", kind: "client" },
  { day: 10, time: "All day", title: "QA + polish", kind: "product" },
  { day: 12, time: "14:00", title: "Final review", detail: "Meet · 30 min", kind: "client" },
  { day: 14, time: "17:00", title: "We go live", detail: "Ship it", kind: "launch" },
];

const DAY_FORMATTER = new Intl.DateTimeFormat("en", { weekday: "short" });
const SHORT_DATE_FORMATTER = new Intl.DateTimeFormat("en", { month: "short", day: "numeric" });
const MONTH_FORMATTER = new Intl.DateTimeFormat("en", { month: "long" });

function createSprintDates(start: Date) {
  return Array.from({ length: 14 }, (_, index) => {
    const date = new Date(start);
    date.setDate(start.getDate() + index);
    return date;
  });
}

function formatCalendarTitle(dates: Date[]) {
  const first = dates[0];
  const last = dates[dates.length - 1];
  if (!first || !last) return "Your next 14 days";

  const firstMonth = MONTH_FORMATTER.format(first);
  const lastMonth = MONTH_FORMATTER.format(last);
  if (first.getFullYear() !== last.getFullYear()) {
    return `${firstMonth} ${first.getFullYear()} to ${lastMonth} ${last.getFullYear()}`;
  }
  if (first.getMonth() !== last.getMonth()) {
    return `${firstMonth} to ${lastMonth} ${last.getFullYear()}`;
  }
  return `${firstMonth} ${first.getFullYear()}`;
}

function eventTone(kind: CalendarEvent["kind"]) {
  if (kind === "launch") return "bg-foreground text-background";
  if (kind === "client") return "bg-electric text-paper";
  return "bg-secondary text-secondary-foreground";
}

function storyTransition(delay: number, reduceMotion: boolean) {
  return reduceMotion
    ? { duration: 0 }
    : { duration: 0.42, delay, ease: [0.2, 0, 0, 1] as const };
}

function Attendees() {
  return (
    <span className="flex -space-x-1" aria-label="Jon and you">
      <span className="grid size-4 place-items-center rounded-full bg-paper text-[7px] font-semibold text-ink ring-1 ring-foreground/10">
        J
      </span>
      <span className="grid size-4 place-items-center rounded-full bg-muted text-[7px] font-semibold text-muted-foreground ring-1 ring-foreground/10">
        Y
      </span>
    </span>
  );
}

function EventCard({ event, index, story }: { event: CalendarEvent; index: number; story: StoryState }) {
  const visible = story.entered || story.reduceMotion;

  return (
    <motion.div
      initial={false}
      animate={visible ? { opacity: 1, y: 0, scale: 1, filter: "blur(0px)" } : { opacity: 0, y: 8, scale: 0.96, filter: "blur(3px)" }}
      transition={storyTransition(0.72 + index * 0.13, story.reduceMotion)}
      className={`relative z-10 rounded-lg px-2 py-1.5 shadow-sm ${eventTone(event.kind)}`}
    >
      <div className="flex items-center justify-between gap-1">
        <span className="truncate text-[10px] font-semibold leading-tight sm:text-[11px]">{event.title}</span>
        {event.kind === "launch" ? (
          <motion.span
            initial={false}
            animate={visible ? { opacity: 1, scale: 1, filter: "blur(0px)" } : { opacity: 0, scale: 0.25, filter: "blur(4px)" }}
            transition={storyTransition(1.82, story.reduceMotion)}
          >
            <Sparkles aria-hidden className="size-3 shrink-0 text-electric-bright" />
          </motion.span>
        ) : event.kind === "client" ? (
          <Camera aria-hidden className="size-3 shrink-0 opacity-75" />
        ) : null}
      </div>
      <div className="mt-1 flex items-center justify-between gap-1 text-[8px] leading-none opacity-70 sm:text-[9px]">
        <span className="tabular-nums">{event.time}</span>
        {event.kind === "client" ? <Attendees /> : null}
      </div>
      {event.moved ? (
        <motion.span
          initial={false}
          animate={visible ? { opacity: 1, y: 0 } : { opacity: 0, y: 3 }}
          transition={storyTransition(1.28, story.reduceMotion)}
          className="absolute -right-1.5 -top-1.5 rounded-full bg-background px-1.5 py-0.5 text-[7px] font-medium text-muted-foreground shadow-sm"
        >
          moved
        </motion.span>
      ) : null}
    </motion.div>
  );
}

function WindowChrome({ title, story }: { title: string; story: StoryState }) {
  const visible = story.entered || story.reduceMotion;
  return (
    <motion.div
      initial={false}
      animate={visible ? { opacity: 1, y: 0 } : { opacity: 0, y: -5 }}
      transition={storyTransition(0.08, story.reduceMotion)}
      className="flex h-12 items-center border-b border-border/70 bg-secondary/50 px-3 sm:h-14 sm:px-4"
    >
      <div className="flex gap-1.5" aria-hidden>
        <span className="size-2.5 rounded-full bg-destructive/70" />
        <span className="size-2.5 rounded-full bg-electric/35" />
        <span className="size-2.5 rounded-full bg-foreground/20" />
      </div>
      <div className="ml-4 hidden items-center gap-1 sm:flex">
        <span className="grid size-10 place-items-center rounded-md text-muted-foreground"><ChevronLeft className="size-4" aria-hidden /></span>
        <span className="grid size-10 place-items-center rounded-md text-muted-foreground"><ChevronRight className="size-4" aria-hidden /></span>
        <span className="ml-1 grid min-h-10 place-items-center rounded-md bg-background px-3 text-[10px] font-medium text-foreground shadow-sm">Today</span>
      </div>
      <div className="pointer-events-none absolute left-1/2 w-[46%] -translate-x-1/2 text-center">
        <p className="truncate text-xs font-semibold text-foreground sm:text-sm">{title}</p>
        <p className="hidden text-[9px] text-muted-foreground sm:block">Your 2-week product sprint</p>
      </div>
      <div className="ml-auto hidden rounded-md bg-background p-0.5 text-[9px] text-muted-foreground shadow-sm sm:flex">
        <span className="rounded bg-secondary px-2 py-1.5 text-foreground">Month</span>
        <span className="px-2 py-1.5">List</span>
      </div>
    </motion.div>
  );
}

function Sidebar({ story }: { story: StoryState }) {
  const calendars = [["Product", "bg-foreground"], ["Client", "bg-electric"], ["Launch", "bg-electric-bright"]];
  const visible = story.entered || story.reduceMotion;
  return (
    <aside className="hidden w-36 shrink-0 border-r border-border/70 bg-secondary/25 p-4 lg:block">
      <motion.p initial={false} animate={{ opacity: visible ? 1 : 0 }} transition={storyTransition(0.28, story.reduceMotion)} className="text-[10px] font-semibold uppercase text-muted-foreground">My calendars</motion.p>
      <ul className="mt-3 space-y-2.5">
        {calendars.map(([label, color], index) => (
          <motion.li key={label} initial={false} animate={visible ? { opacity: 1, x: 0 } : { opacity: 0, x: -5 }} transition={storyTransition(0.36 + index * 0.1, story.reduceMotion)} className="flex items-center gap-2 text-[11px] text-foreground">
            <motion.span initial={false} animate={visible ? { scale: 1 } : { scale: 0.25 }} transition={storyTransition(0.4 + index * 0.1, story.reduceMotion)} className={`grid size-4 place-items-center rounded-sm ${color}`}>
              <Check className="size-2.5 text-paper" strokeWidth={2.5} aria-hidden />
            </motion.span>
            {label}
          </motion.li>
        ))}
      </ul>
      <motion.div initial={false} animate={visible ? { opacity: 1, y: 0 } : { opacity: 0, y: 6 }} transition={storyTransition(0.62, story.reduceMotion)} className="mt-8 rounded-lg bg-background p-2.5 shadow-sm">
        <p className="text-[9px] text-muted-foreground">Sprint progress</p>
        <p className="mt-1 text-2xl font-medium tabular-nums text-foreground">14d</p>
        <div className="mt-2 h-1 overflow-hidden rounded-full bg-muted"><motion.div initial={false} animate={{ width: visible ? "75%" : "0%" }} transition={storyTransition(0.68, story.reduceMotion)} className="h-full rounded-full bg-electric" /></div>
      </motion.div>
    </aside>
  );
}

function TimelinePath({ story }: { story: StoryState }) {
  const visible = story.entered || story.reduceMotion;
  return (
    <svg className="pointer-events-none absolute inset-0 z-0 h-full w-full" viewBox="0 0 700 200" preserveAspectRatio="none" aria-hidden>
      <motion.path
        d="M 50 50 H 650 C 685 50 685 150 650 150 H 50"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeDasharray="5 7"
        className="text-electric/30"
        initial={false}
        animate={{ pathLength: visible ? 1 : 0, opacity: visible ? 1 : 0 }}
        transition={story.reduceMotion ? { duration: 0 } : { duration: 1.25, delay: 0.58, ease: [0.2, 0, 0, 1] }}
      />
    </svg>
  );
}

function CurrentTimeLine({ now }: { now: Date }) {
  const hour = now.getHours() + now.getMinutes() / 60;
  const position = Math.min(86, Math.max(40, 40 + ((hour - 8) / 10) * 44));
  return (
    <div className="absolute inset-x-0 z-20 flex items-center" style={{ top: `${position}%` }} aria-label={`Current time ${now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`}>
      <span className="size-1.5 -translate-x-1/2 rounded-full bg-destructive" />
      <span className="h-px flex-1 bg-destructive/50" />
    </div>
  );
}

function DesktopCalendar({ dates, now, story }: { dates: Date[]; now: Date; story: StoryState }) {
  const visible = story.entered || story.reduceMotion;

  const renderCell = (date: Date, day: number, index: number) => {
    const event = EVENTS.find((item) => item.day === day);
    const weekend = date.getDay() === 0 || date.getDay() === 6;
    const isToday = day === 1;
    return (
      <motion.div
        key={date.toISOString()}
        initial={false}
        animate={{ opacity: visible ? 1 : 0 }}
        transition={storyTransition(0.22 + index * 0.025, story.reduceMotion)}
        className={`relative min-h-32 border-b border-r border-border/70 p-1.5 last:border-r-0 xl:min-h-36 xl:p-2 ${weekend ? "bg-secondary/35" : "bg-background"}`}
      >
        <div className="relative z-10 mb-2 flex items-center justify-between">
          <div className="flex min-w-0 items-center gap-1.5">
            <span className={`grid size-6 shrink-0 place-items-center rounded-full text-[10px] tabular-nums ${isToday ? "bg-electric text-paper shadow-sm" : "text-muted-foreground"}`}>{date.getDate()}</span>
            {isToday ? <span className="text-[7px] font-semibold uppercase text-electric">Today</span> : null}
          </div>
        </div>
        {event ? <EventCard event={event} index={EVENTS.indexOf(event)} story={story} /> : null}
        {day === 3 ? <span className="absolute bottom-2 left-2 z-10 text-[8px] text-muted-foreground/60">Deep work</span> : null}
        {day === 7 ? <span className="absolute bottom-2 left-2 z-10 text-[8px] text-muted-foreground/60">No meetings</span> : null}
        {isToday && !weekend ? <CurrentTimeLine now={now} /> : null}
      </motion.div>
    );
  };

  const weekBanner = (n: number, start: Date | undefined, end: Date | undefined, delay: number) => (
    <motion.div
      initial={false}
      animate={{ opacity: visible ? 1 : 0 }}
      transition={storyTransition(delay, story.reduceMotion)}
      className="col-span-7 flex items-center justify-between border-b border-border/70 bg-secondary/15 px-3 py-1.5 text-[8px] font-medium uppercase tracking-wide text-muted-foreground"
    >
      <span>Sprint week {n}</span>
      <span className="tabular-nums">{start && end ? `${SHORT_DATE_FORMATTER.format(start)} to ${SHORT_DATE_FORMATTER.format(end)}` : null}</span>
    </motion.div>
  );

  return (
    <div className="hidden min-w-0 flex-1 md:block">
      <div className="grid grid-cols-7 border-b border-border/70 bg-secondary/20">
        {dates.slice(0, 7).map((date, index) => (
          <motion.div key={date.toISOString()} initial={false} animate={{ opacity: visible ? 1 : 0 }} transition={storyTransition(0.2 + index * 0.035, story.reduceMotion)} className="border-r border-border/70 px-2 py-2 text-center text-[9px] font-medium uppercase text-muted-foreground last:border-r-0">
            {DAY_FORMATTER.format(date)}
          </motion.div>
        ))}
      </div>
      <div className="relative grid grid-cols-7">
        <TimelinePath story={story} />
        {weekBanner(1, dates[0], dates[6], 0.18)}
        {dates.slice(0, 7).map((date, i) => renderCell(date, i + 1, i))}
        {weekBanner(2, dates[7], dates[13], 0.5)}
        {dates.slice(7, 14).map((date, i) => renderCell(date, i + 8, i + 7))}
      </div>
    </div>
  );
}

function MobileAgenda({ dates, story }: { dates: Date[]; story: StoryState }) {
  return (
    <div className="p-3 md:hidden">
      <div className="mb-3 flex items-center justify-between px-1">
        <span className="text-[10px] font-semibold uppercase text-muted-foreground">Two-week agenda</span>
        <span className="text-[10px] tabular-nums text-muted-foreground">8 appointments</span>
      </div>
      <ol className="relative space-y-2 before:absolute before:bottom-4 before:left-[17px] before:top-4 before:w-px before:bg-border">
        {EVENTS.map((event, index) => {
          const date = dates[event.day - 1];
          return (
            <motion.li key={event.day} initial={false} animate={(story.entered || story.reduceMotion) ? { opacity: 1, x: 0 } : { opacity: 0, x: -6 }} transition={storyTransition(0.42 + index * 0.12, story.reduceMotion)} className="relative grid grid-cols-[2.25rem_1fr] gap-2">
              <span className={`z-10 grid size-9 place-items-center rounded-full text-center text-[9px] font-semibold leading-tight tabular-nums ${event.kind === "launch" ? "bg-foreground text-background" : event.day === 1 ? "bg-electric text-paper" : "bg-secondary text-muted-foreground"}`}>
                <span><span className="block text-[7px] uppercase opacity-70">{date ? DAY_FORMATTER.format(date) : "Day"}</span>{date?.getDate() ?? event.day}</span>
              </span>
              <EventCard event={event} index={index} story={story} />
            </motion.li>
          );
        })}
      </ol>
    </div>
  );
}

function CalendarSkeleton() {
  return (
    <div className="h-[558px] animate-pulse bg-secondary/20 md:h-[338px]" aria-label="Preparing your local sprint calendar" />
  );
}

export function SprintCalendar() {
  const [now, setNow] = useState<Date | null>(null);
  const calendarRef = useRef<HTMLDivElement>(null);
  const inView = useInView(calendarRef, { once: true, amount: 0.28 });
  const reduceMotion = Boolean(useReducedMotion());

  useEffect(() => {
    const localNow = new Date();
    localNow.setSeconds(0, 0);
    setNow(localNow);
  }, []);

  const dates = useMemo(() => (now ? createSprintDates(now) : []), [now]);
  const story = { entered: inView, reduceMotion };

  return (
    <div ref={calendarRef}>
      <motion.div
        role="img"
        aria-label="A live two-week product sprint calendar beginning today and ending with launch on day 14."
        initial={false}
        animate={(inView || reduceMotion) ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 14, scale: 0.985 }}
        transition={storyTransition(0, reduceMotion)}
        className="relative overflow-hidden rounded-[1.35rem] bg-card shadow-[0_0_0_1px_color-mix(in_oklab,var(--foreground)_8%,transparent),0_20px_60px_-28px_color-mix(in_oklab,var(--foreground)_32%,transparent)]"
      >
        {now ? (
          <>
            <WindowChrome title={formatCalendarTitle(dates)} story={story} />
            <div className="flex">
              <Sidebar story={story} />
              <DesktopCalendar dates={dates} now={now} story={story} />
              <MobileAgenda dates={dates} story={story} />
            </div>
            <motion.div initial={false} animate={(inView || reduceMotion) ? { opacity: 1, y: 0 } : { opacity: 0, y: 6 }} transition={storyTransition(1.92, reduceMotion)} className="flex items-center gap-3 border-t border-border/70 bg-secondary/30 px-4 py-3 sm:px-5">
              <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-background text-electric shadow-sm"><CalendarDays className="size-3.5" aria-hidden /></span>
              <div className="min-w-0">
                <p className="text-[10px] text-muted-foreground">Next up</p>
                <p className="truncate text-xs font-medium text-foreground">Sprint 02 · starts {DAY_FORMATTER.format(dates[13] ? createSprintDates(dates[13])[1] ?? dates[13] : now)}</p>
              </div>
              <span className="ml-auto hidden rounded-full bg-background px-2.5 py-1 text-[9px] font-medium text-muted-foreground shadow-sm xs:inline">Repeats every 2 weeks</span>
            </motion.div>
          </>
        ) : <CalendarSkeleton />}
      </motion.div>
      <motion.div initial={false} animate={(inView || reduceMotion) ? { opacity: 1, y: 0 } : { opacity: 0, y: 4 }} transition={storyTransition(2.05, reduceMotion)} className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 px-1 text-[10px] text-muted-foreground">
        <span className="flex items-center gap-1.5"><span className="size-2 rounded-full bg-electric" /> Calls with you</span>
        <span className="flex items-center gap-1.5"><span className="size-2 rounded-full bg-foreground" /> Launch</span>
        <span className="ml-auto hidden sm:inline">14 days from kickoff to live product</span>
      </motion.div>
    </div>
  );
}